// ============================================================================
// src/app/appRoutes.ts — the application's own top-level route table.
// ============================================================================
// B3.1 finish, last step. The six domain tables cover the program surfaces;
// these eighteen are App's own views — the home dashboard, the cross-program
// operational surfaces, and the admin surfaces. They were the last
// `view === 'X'` clauses left in App().
//
// The seven tables PARTITION the view space: no view appears in two of them.
// That is what makes collecting these clauses into one block safe regardless of
// where the domain dispatches sit, and it is asserted rather than assumed (see
// tests/static/app_routes_static.test.js).
//
// Three things in the dispatch are deliberately NOT here, because they are not
// keyed on `view` and a route table cannot express them:
//
//   1. Light containment — any view a Light user may not reach renders the
//      Light portal instead. It runs before every route.
//   2. `showForm && !pathname.startsWith('/broiler/batches/')` — the
//      full-screen broiler batch form. Keyed on form state, and it must stay
//      ahead of the broiler dispatch so the record page keeps owning its own
//      embedded form.
//
//      That branch SPLITS this table, and the split is load-bearing. Before the
//      route extraction, five of these clauses (home, animalHistory, production,
//      weather, mySubmissions) sat ABOVE the form branch and eleven sat below.
//      Those two numbers are HISTORICAL and stay as they are; rows added since
//      join the lower half without changing what the extraction found.
//      Collapsing all sixteen into one dispatch below the branch changed which
//      predicate wins: with the drawer open, a Back/Forward navigation to one of
//      those five rendered the destination before, and kept the form after. So
//      the table is exported in two halves and dispatched in two places. The
//      halves are disjoint and their union is APP_ROUTES, so nothing else moves.
//   3. The LoginScreen gate, which must stay ahead of the webforms dispatch.
//      Two guards pin that ordering in two required halves each.
//
// Path notes:
//   weighinsessions  has NO VIEW_TO_PATH entry at all. It is URL-reachable
//                    only, at /weigh-in-sessions/<id>, so its canonical path
//                    exists here and nowhere else.
//   equipmentHome    owns /fleet/* — EquipmentHome does its own sub-routing for
//                    the fuel log and per-equipment record pages.
//   tasks            owns /tasks/* — the Task Center owns its record pages.
//   processingCutSheet
//                    owns /processing/cut-sheet/* and, like weighinsessions,
//                    has NO VIEW_TO_PATH entry: every address under it names a
//                    Processing record, so there is no bare path to register.
//                    /processing itself stays exact and is unaffected.
//   /admin           is webforms, and /admin/client-errors and
//                    /admin/newsletter are separate exact paths beneath it.
//                    All three are exact, so none can swallow the others.
//   equipmentAdminRecord
//                    owns /admin/equipment/* (A40) and, like
//                    processingCutSheet, has NO VIEW_TO_PATH entry: every
//                    address under it names one piece of equipment. Its one
//                    accepted shape is fenced in
//                    src/app/boot/adminEquipmentRecordPath.ts. It is deeper
//                    than the exact /admin row and shares no prefix with the
//                    other two admin paths.
// ============================================================================

// The five that must be dispatched ABOVE the full-screen broiler form branch,
// because they were above it before the extraction. Order within the half is
// the original clause order.
export const APP_ROUTES_PRE_FORM = [
  {view: 'home', paths: ['/']},
  {view: 'animalHistory', paths: ['/animals-on-farm']},
  {view: 'production', paths: ['/production']},
  {view: 'weather', paths: ['/weather']},
  {view: 'mySubmissions', paths: ['/my-submissions']},
];

// The fourteen that are below the form branch and stay below it.
export const APP_ROUTES_POST_FORM = [
  {view: 'weighinsessions', paths: ['/weigh-in-sessions/*']},
  {view: 'broilerweighins', paths: ['/broiler/weighins']},
  {view: 'pigweighins', paths: ['/pig/weighins']},
  {view: 'equipmentHome', paths: ['/fleet/*']},
  {view: 'pastureMap', paths: ['/pasture-map']},
  {view: 'processing', paths: ['/processing']},
  // The cut-sheet editor is a RECORD SPACE, not a second Processing screen:
  // every address under it names one Processing record, and there is no hub
  // beneath the prefix. That makes it the `weighinsessions` arrangement — a
  // splat row with a single mounted component and no VIEW_TO_PATH entry —
  // rather than the `tasks` one. `/processing` above stays EXACT, so it cannot
  // be swallowed by this row, and this row is deeper so it wins over any
  // ancestor splat. The one pathname shape it claims is fenced in
  // `src/app/boot/cutSheetRecordPaths.ts`.
  {view: 'processingCutSheet', paths: ['/processing/cut-sheet/*']},
  {view: 'tasks', paths: ['/tasks/*']},
  {view: 'activity', paths: ['/activity']},
  {view: 'projects', paths: ['/projects']},
  {view: 'clientErrors', paths: ['/admin/client-errors']},
  {view: 'webforms', paths: ['/admin']},
  {view: 'newsletterAdmin', paths: ['/admin/newsletter']},
  // A40: the Admin equipment record page, a record space like the cut-sheet
  // editor (splat, single component, no VIEW_TO_PATH entry).
  {view: 'equipmentAdminRecord', paths: ['/admin/equipment/*']},
];

// The whole App-owned surface, in the original clause order. Consumers that
// care about the eighteen as a set — the partition guard, the declarative
// oracle, AppEntry's own route rendering — read this and are unaffected by
// where the dispatch is split.
export const APP_ROUTES = [...APP_ROUTES_PRE_FORM, ...APP_ROUTES_POST_FORM];
